import React from 'react'
import { Page, Text, View, Document, Image, StyleSheet, PDFViewer, Font, PDFDownloadLink } from '@react-pdf/renderer';
import { Box } from '@mui/system';
import ActionButton from '../../../../components/buttons/ActionButton/ActionButton.component';
import SourceSerifPro from '../../../../fonts/SourceSerifPro-Regular.ttf';
import moment from 'moment';

// utils
import { getForemanAssignment, cleanJobDayAssignments } from './PDFGenerator.collection.utils';

// assets
import logo from '../../../../assets/images/logo.jpg'

// mui
import { Alert, AlertTitle } from '@mui/material';

Font.register({ family: "source-serif-pro", src: SourceSerifPro})

const workTypeChoices = {
    TM: "T & M",
    CW: "Contract Work"
}

const workType2Choices = {
    IN: "Interior",
    E: "Exterior"
}

const workType1Choices = {
    E: "Erect",
    D: "Dismantle"
}

// Create styles
const styles = StyleSheet.create({
  page: {
    fontFamily: "source-serif-pro",
    fontSize: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "20px",
  },
  logo: {
    width: "78px"
  },
  title: {
    width: "100%",
    textAlign: "center",
    fontSize: "24px",
    marginBottom: "8px"
  },
  multiColumnContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "8px",
    paddingTop: "8px",
    textAlign: "left"
  },
  leftAlignedColumn: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    textAlign: "left",
    flex: 1
  },
  leftColumn: {
    flex: 2
  },
  middleColumn: {
    flex: 1
  },
  rightColumn: {
    flex: 1,
    textAlign: "right"
  },
  columnOne: {
    flex: 1,
    marginRight: "16px"
  },
  columnTwo: {
    flex: 1
  },
  horizontalRule: {
    height: "2px",
    width: "100%",
    backgroundColor: "black",
  },
  crewAssignmentsTable: {
    border: "1px solid black",
    width: "100%",
    padding: "8px 16px"
  },
  crewAssignmentsTableRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    marginTop: "2px",
    height: "24px"
  },
  bottomSignature: {
    marginTop: "16px",
    width: "50%"
  },
  ticketStatus: {
    marginTop: "24px",
    width: "100%",
    paddingLeft: "16px",
    textAlign: "left"
  },
  largerFont: {
    fontSize: "16px"
  },
  customerComments: {
    marginTop: "16px"
  }
});

/**
 * Calculate total man hours from cleaned assignments
 * This sums ST + OT + DT for all crew members
 * Then deducts 0.5 hours (lunch) for each crew member who has ST > 0
 * 
 * @param {Array} cleanedAssignments - Array of cleaned assignment objects
 * @returns {string} - Total hours formatted to 1 decimal place
 */
const calculateTotalManHoursWithLunchDeduction = (cleanedAssignments) => {
    let totalST = 0;
    let totalOT = 0;
    let totalDT = 0;
    let lunchDeductionCount = 0; // Count of crew members who need lunch deducted
    
    cleanedAssignments.forEach(assignment => {
        // Parse ST value
        let st = 0;
        if (assignment.hours !== "" && assignment.hours !== undefined && assignment.hours !== null) {
            const parsed = parseFloat(assignment.hours);
            st = isNaN(parsed) ? 0 : parsed;
        }
        
        // Parse OT value
        let ot = 0;
        if (assignment.overTime !== "" && assignment.overTime !== undefined && assignment.overTime !== null) {
            const parsed = parseFloat(assignment.overTime);
            ot = isNaN(parsed) ? 0 : parsed;
        }
        
        // Parse DT value
        let dt = 0;
        if (assignment.doubleTime !== "" && assignment.doubleTime !== undefined && assignment.doubleTime !== null) {
            const parsed = parseFloat(assignment.doubleTime);
            dt = isNaN(parsed) ? 0 : parsed;
        }
        
        totalST += st;
        totalOT += ot;
        totalDT += dt;
        
        // Count crew members who have ST hours > 0
        // Each crew member with ST > 0 should have 0.5 hours lunch deducted
        if (st > 0) {
            lunchDeductionCount++;
        }
    });
    
    // Calculate total lunch deduction (0.5 hours per crew member with ST)
    const totalLunchDeduction = 0.5 * lunchDeductionCount;
    
    // Total Hours = ST + OT + DT - (0.5 * number of crew members with ST)
    const total = totalST + totalOT + totalDT - totalLunchDeduction;
    
    // Ensure we don't return negative values
    const finalTotal = total < 0 ? 0 : total;
    
    return finalTotal.toFixed(1);
};

const PDFGenerator = ({ data, loading, error }) => {
    if(loading) return null

    if(error) {
      return (<Alert severity="error">
        <AlertTitle>Unable to Generate PDF</AlertTitle>
        {error.message}
      </Alert>)
    }

    const { jobDay } = data;
    const assignments = cleanJobDayAssignments(jobDay.crewMemberAssignments)
    const foremanAssignment = getForemanAssignment(jobDay.crewMemberAssignments)

    const ticketNumber = `${jobDay.id.toString().padStart(3, '0')}-${moment(jobDay.date).format("MMDDYY")}`
    
    // Calculate total hours with lunch deduction (0.5 per crew member with ST)
    const totalManHours = calculateTotalManHoursWithLunchDeduction(assignments);

    const pdfDoc = (
      <Document title={`ASI Scaffolds - ${jobDay.id.toString().padStart(3, '0')}-${moment(jobDay.date).format("MMDDYY")} - Receipt`}>
        <Page size="LETTER" style={styles.page}>

          <View style={styles.multiColumnContainer}>
            <View style={styles.leftColumn}>
              <View style={styles.logo} >
                <Image src={logo}></Image>
              </View>
            </View>
            <View style={styles.middleColumn}></View>
            <View style={styles.rightColumn}>
              <Text>{jobDay.job.wageRate}</Text>
              <Text>Total Hours: {totalManHours}</Text>
            </View>
          </View>

          <View style={styles.title}>
            <Text>Time Ticket</Text>
          </View>
          
          <View style={styles.multiColumnContainer}>
              <View style={styles.leftColumn}>
                  <Text style={styles.largerFont}>{jobDay.job.customerName}</Text>
                  <Text style={styles.largerFont}>{jobDay.job.name}</Text>
                  <Text>{jobDay.job.street1} {jobDay.job.street2}</Text>
                  <Text>{jobDay.job.city}{jobDay.job.city ? "," : ""} {jobDay.job.state} {jobDay.job.zipcode}</Text>
              </View>

              <View style={styles.middleColumn}>
                  <Text>{workTypeChoices[jobDay.workType]}</Text>
                  <Text>{workType1Choices[jobDay.workType1]}</Text>
                  <Text>{workType2Choices[jobDay.workType2]}</Text>
              </View>

              <View style={styles.rightColumn}>
                  <Text style={styles.largerFont}>{ticketNumber}</Text>
                  <Text>{jobDay.job.customerContactName}</Text>
                  <Text>{jobDay.job.customerPhone}</Text>
              </View>
          </View>

          <View style={styles.multiColumnContainer}>
              <Text style={styles.leftColumn}>Truck: #{jobDay.truck ? jobDay.truck.number : ""}</Text>
              <Text style={styles.middleColumn}>Driver: {jobDay.driver ? `${jobDay.driver.firstName} ${jobDay.driver.lastName}` : ""}</Text>
              <Text style={styles.rightColumn}>Frames: {jobDay.numFrames}</Text>
          </View>
          <View style={styles.horizontalRule}></View>

          <View style={styles.multiColumnContainer}>
              <View style={styles.columnOne}>
                  <Text style={styles.columnHeader}>Scope of Work</Text>
                  <Text>{jobDay.scopeOfWork}</Text>
              </View>
              <View style={styles.columnTwo}>
                  <Text style={styles.columnHeader}>Safety</Text>
                  <Text>{jobDay.safetyNotes}</Text>
              </View>
          </View>
          
          <View style={styles.crewAssignmentsTable}>
              <View style={styles.crewAssignmentsTableRow}>
                  <Text style={{ flex: 2 }}>Name</Text>
                  <Text style={{ flex: 2 }}>Time</Text>
                  <Text style={{ flex: 1 }}>ST</Text>
                  <Text style={{ flex: 1 }}>OT</Text>
                  <Text style={{ flex: 1 }}>DT</Text>
                  <Text style={{ flex: 2 }}>Signature</Text>
              </View>

              <View style={styles.horizontalRule}></View>

              {
                  assignments.map((assignment, index) => {
                      return (<View key={assignment.id || index} style={styles.crewAssignmentsTableRow}>
                          <Text style={{ flex: 2 }}>{assignment.name}</Text>
                          <Text style={{ flex: 2 }}>{assignment.time}</Text>
                          <Text style={{ flex: 1 }}>{assignment.hours}</Text>
                          <Text style={{ flex: 1 }}>{assignment.overTime}</Text>
                          <Text style={{ flex: 1 }}>{assignment.doubleTime}</Text>
                          <View style={{ flex: 2 }}>{ assignment.signOff && assignment.signOff.signature && <Image src={assignment.signOff.signature}></Image> }</View>
                      </View>)
                  })
              }
              
          </View>

          <View style={styles.multiColumnContainer}>
            <View style={{ flex: 1}}>
              <Text>Foreman - {foremanAssignment && foremanAssignment.crewMember ? foremanAssignment.crewMember.firstName : ""} {foremanAssignment && foremanAssignment.crewMember ? foremanAssignment.crewMember.lastName : ""}</Text>
                {
                  foremanAssignment && 
                  foremanAssignment.signOff && 
                  <Image style={styles.bottomSignature} src={foremanAssignment.signOff.signature}></Image>
                }
            </View>

            <View style={{ flex: 1 }}>
                <Text>{ jobDay.job.customerContactName ? jobDay.job.customerContactName : "No Customer Contact Name Provided" }</Text>
                { jobDay.customerSignature && <Image style={styles.bottomSignature} src={jobDay.customerSignature ? jobDay.customerSignature : "#"}></Image>}
                <Text style={styles.customerComments}>{ jobDay.customerComments }</Text>
            </View>
          </View>
        </Page>
      </Document>
    )

    return (<Box sx={{ width: "100%", height: "80vh"}}>
      <Box sx={{ marginTop: "24px", marginBottom: "24px"}}>
        <PDFDownloadLink document={pdfDoc} fileName={`ASI Scaffolds - ${jobDay.id.toString().padStart(3, '0')}-${moment(jobDay.date).format("MMDDYY")} - Receipt.pdf`}>
          {({ loading }) =>
            loading ? 'Loading document...' : <ActionButton color='primary' label='Download' />
          }
        </PDFDownloadLink>
      </Box>

      <PDFViewer width={"100%"} height={"100%"}>
        { pdfDoc }
      </PDFViewer>
    </Box>)
}

export default PDFGenerator;
